import { useState } from 'react'
import {
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  useTheme
} from '@mui/material'
import {
  Level1IconButton,
  Level2IconButton
} from 'components/atoms/button/JourneyPathIconButton'
import StyledAccordion from 'components/atoms/container/Accordion'
import InCardStack from 'components/atoms/container/InCardStack'
import RowStack from 'components/atoms/container/RowStack'
import IconTextGroup from 'components/atoms/container/IconTextGroup'
import WatchLaterOutlinedIcon from '@mui/icons-material/WatchLaterOutlined'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import { formatSeconds } from 'utils/formatDateTime'
import colors from 'styles/colors'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import { useTranslation } from 'react-i18next'

const JourneyStepCard = ({
  l1,
  index,
  goalSteps,
  currentStepNumber,
  journeyCompleted,
  activeStepNumber,
  activeL1StepNumber,
  clickHandler
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [expanded, setExpanded] = useState(l1?.number === activeL1StepNumber)

  const percentComplete =
    l1.childL2Steps.reduce((acc, l2) => {
      if (l2.number < currentStepNumber) {
        return acc + 1
      }
      return acc
    }, 0) / l1.childL2Steps.length

  const lastL1Step = index === goalSteps.length - 1
  const activeL1Step =
    !journeyCompleted &&
    currentStepNumber > l1.number &&
    !lastL1Step &&
    goalSteps[index + 1].number > currentStepNumber
  const completedL1Step = l1.number <= currentStepNumber && !activeL1Step

  return (
    <StyledAccordion
      key={l1.number}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      expandIconColor={theme.palette.primary.main}
      sx={{
        boxShadow: 'none',
        backgroundColor: colors.White,
        borderRadius: 4,
        border: '1px solid',
        width: '100%',
        overflow: 'hidden',
        borderColor: activeL1Step ? colors.GoldenYellow300 : colors.Grey100,
        '::before': {
          display: 'none'
        },
        '& .MuiAccordionSummary-root': {
          padding: '12px 16px',
          backgroundColor: activeL1Step
            ? colors.GoldenYellow50
            : expanded
              ? colors.Grey25
              : colors.White,

          '& .MuiAccordionSummary-content': {
            margin: 0
          },

          '& .MuiAccordionSummary-expandIconWrapper': {
            display: lastL1Step ? 'none' : 'flex'
          }
        },
        '& .MuiAccordionDetails-root': {
          padding: '8px 16px 8px 20px',
          background: activeL1Step ? '#FFFEFC' : colors.White
        },
        '&:first-of-type': {
          borderRadius: 4
        },
        '&:last-of-type': {
          borderRadius: 4,
          ...(lastL1Step
            ? {
                backgroundColor: colors.White,
                pointerEvents: 'none',
                marginBottom: 10
              }
            : {})
        },
        '&.Mui-expanded': {
          margin: '12px 0 0 0'
        }
      }}
      summaryComponent={
        <RowStack
          spacing={8}
          alignItems={!lastL1Step ? 'flex-start' : 'center'}>
          <Level1IconButton
            completed={completedL1Step}
            active={activeL1Step}
            percentComplete={percentComplete}
            lastL1Step={lastL1Step}
          />
          <InCardStack spacing={0} alignItems={'flex-start'} flex={1}>
            <Typography variant='h4'>{l1.name}</Typography>

            {!lastL1Step && (
              <IconTextGroup
                color={colors.Grey350}
                group={[
                  {
                    icon: <WatchLaterOutlinedIcon />,
                    text: formatSeconds(l1.lengthSeconds)
                  },
                  {
                    icon: <StickyNote2OutlinedIcon />,
                    text: `${l1?.childL2Steps?.length} ${t('pathsAndModels.steps')}`
                  }
                ]}
              />
            )}
          </InCardStack>
        </RowStack>
      }>
      <Stepper
        orientation='vertical'
        connector={null}
        sx={{
          paddingX: 4
        }}>
        {l1.childL2Steps.map((l2, l2Index) => {
          const stepCompleted = currentStepNumber >= l2.number
          const stepActive = activeStepNumber === l2.number
          return (
            <Step
              key={l2.number}
              id={`step-${l2.number}`}
              onClick={() => stepCompleted && clickHandler(index, l2Index)}
              sx={{
                paddingY: 8,
                '& .MuiStepLabel-root': {
                  position: 'relative',
                  padding: 0,
                  alignItems: 'flex-start',
                  '&:hover': {
                    cursor: stepCompleted || stepActive ? 'pointer' : 'default'
                  },
                  '& .MuiStepLabel-iconContainer': {
                    zIndex: 1
                  }
                }
              }}>
              <StepLabel
                icon={
                  <>
                    <Level2IconButton
                      completed={stepCompleted}
                      active={stepActive}
                      learningCheck={
                        l2.isLastL2Step &&
                        l2.childL3Steps?.some(step => step.type === 'quiz')
                      }
                    />
                    {l2Index < l1.childL2Steps.length - 1 && (
                      <StepConnector
                        sx={{
                          position: 'absolute',
                          top: 30, // same as height of icon
                          marginLeft: 7.5,
                          height: '100%',

                          '& .MuiStepConnector-line': {
                            minHeight: '110%',
                            borderLeftStyle: 'dashed',
                            borderLeftColor: stepCompleted
                              ? theme.palette.secondary.main
                              : colors.Grey350
                          }
                        }}
                      />
                    )}
                  </>
                }>
                <InCardStack spacing={0} alignItems={'flex-start'}>
                  <RowStack
                    justifyContent={'space-between'}
                    alignItems={'flex-start'}
                    color={
                      stepActive ? theme.palette.primary.main : colors.Grey350
                    }
                    fontWeight={stepActive ? 600 : 500}>
                    <Typography
                      variant='body1'
                      color={'inherit'}
                      fontWeight={'inherit'}
                      flex={1}>
                      {l2.name}
                    </Typography>
                    <Typography
                      variant='body1Small'
                      color={'inherit'}
                      fontWeight={'inherit'}>
                      {formatSeconds(l2.lengthSeconds, 'mm:ss')}
                    </Typography>
                  </RowStack>

                  {l2.currentUserCount && (
                    <RowStack spacing={2}>
                      <PersonOutlineOutlinedIcon
                        sx={{ color: colors.Grey350, fontSize: 20 }}
                      />
                      <Typography variant='body1Small' color={colors.Grey350}>
                        {l2.currentUserCount}
                      </Typography>
                    </RowStack>
                  )}
                </InCardStack>
              </StepLabel>
            </Step>
          )
        })}
      </Stepper>
    </StyledAccordion>
  )
}

export default JourneyStepCard
